import { forwardRef } from 'react';
import {
  FieldErrorProps,
  Group,
  GroupProps as AriaGroupProps,
  InputProps,
  TextAreaProps,
  LabelProps as AriaLabelProps,
  FieldError as AriaFieldError,
  Input as AriaInput,
  TextArea as AriaTextArea,
  Label as AriaLabel,
  Text,
  TextProps,
  composeRenderProps,
} from 'react-aria-components';
import { twMerge } from 'tailwind-merge';
import { tv } from 'tailwind-variants';
import { composeTailwindRenderProps, focusRing } from 'src/utils';
import RequiredIcon from 'src/assets/icons/asterisk.svg';

import type { ColorVariant, Size } from 'src/types/common';

interface LabelProps extends AriaLabelProps {
  isRequired?: boolean;
}

export const Label = ({
  isRequired = false,
  className,
  children,
  ...props
}: LabelProps) => {
  return (
    <AriaLabel
      {...props}
      className={twMerge(
        `text-base text-surface-alt-75 font-bold leading-6 cursor-default w-fit ${isRequired ? 'flex items-start gap-0.5' : ''}`,
        className
      )}
    >
      {children}
      {isRequired ? (
        <RequiredIcon aria-label="(required)" className="w-2 h-2 text-red" />
      ) : null}
    </AriaLabel>
  );
};

export const Description = ({ className, ...props }: TextProps) => {
  return (
    <Text
      {...props}
      slot="description"
      className={twMerge('text-xs text-surface-75 font-medium', className)}
    />
  );
};

export const FieldError = ({ className, ...props }: FieldErrorProps) => {
  return (
    <AriaFieldError
      {...props}
      className={composeTailwindRenderProps(
        className,
        'text-xs text-red font-medium'
      )}
    />
  );
};

export const inputStyles = tv({
  extend: focusRing,
  // hover glow is neutral; when the field is focused, isFocused's
  // hover:shadow override keeps the colored focus halo in place.
  base: 'border-2 rounded-md w-full hover:shadow-[0_0_0_4px_var(--halo-hover)] [&[type=search]::-webkit-search-cancel-button]:hidden',
  variants: {
    variant: {
      // Focus ring color comes from focusRing (outline-secondary) — do not
      // override it with ghost tints; they fail 3:1 non-text contrast.
      // The surface border carries the cross-cutting-003 contrast fix here,
      // in the variant, because twMerge lets it beat Input's base classes.
      surface: 'border-surface-alt-60 focus:border-surface-alt-75',
      primary: 'border-primary-ghost-alt-15 focus:border-primary-ghost-alt-45',
      secondary:
        'border-secondary-ghost-alt-15 focus:border-secondary-ghost-alt-45',
      success: 'border-green-ghost-alt-15 focus:border-green-ghost-alt-45',
      danger: 'border-red-ghost-alt-15 focus:border-red-ghost-alt-45',
      warning: 'border-yellow-ghost-alt-15 focus:border-yellow-ghost-alt-45',
      ai: 'border-ai-ghost-alt-15 focus:border-ai-ghost-alt-45',
    },
    size: {
      small: 'min-h-8 px-2 py-1 text-sm',
      medium: 'min-h-11 py-2 text-md',
    },
    // Invalid must stay perceivable in every state, including focused —
    // the red border wins over the variant's focus:border-* darkening.
    isInvalid: {
      true: 'border-red focus:border-red focus-within:border-red outline-red [--focus-halo:var(--halo-red)]',
    },
    isFocused: {
      true: 'outline-2 shadow-[0_0_0_4px_var(--focus-halo)] hover:shadow-[0_0_0_4px_var(--focus-halo)]',
    },
    isDisabled: {
      true: 'border-surface-alt-ghost-15 hover:shadow-none',
    },
    hasIcon: {
      true: 'pl-11',
    },
    rightSlotActive: {
      true: 'pr-11',
    },
  },
  defaultVariants: {
    variant: 'surface',
  },
  compoundVariants: [
    {
      size: 'small',
      hasIcon: true,
      className: 'pl-8',
    },
    {
      size: 'small',
      rightSlotActive: true,
      className: 'pr-8',
    },
  ],
});

export const textareaStyles = tv({
  extend: inputStyles,
  base: 'resize-none',
  variants: {
    size: {
      small: 'h-auto py-2',
      medium: 'h-auto py-3',
    },
  },
});

export const hintStyles = tv({
  variants: {
    variant: {
      surface: 'text-surface-alt-75',
      primary: 'text-primary-ghost-alt-60',
      secondary: 'text-secondary-ghost-alt-60',
      success: 'text-green-ghost-alt-60',
      danger: 'text-red-ghost-alt-60',
      warning: 'text-yellow-ghost-alt-60',
      ai: 'text-ai-ghost-alt-60',
    },
    isInvalid: {
      true: 'border-red',
    },
  },
  defaultVariants: {
    variant: 'surface',
    size: 'medium',
  },
});

export const iconStyles = tv({
  base: 'absolute text-surface-75',
  variants: {
    size: {
      small: 'text-xs',
      medium: 'text-lg',
    },
    isTextArea: {
      true: 'top-3.5',
      false: 'top-1/2 -translate-y-1/2',
    },
  },
  defaultVariants: {
    size: 'medium',
  },
});

export const fieldGroupStyles = tv({
  extend: inputStyles,
  base: 'group flex items-center border-2 rounded-lg overflow-hidden',
});

interface GroupProps extends AriaGroupProps {
  variant?: ColorVariant;
  size?: Size;
}

export const FieldGroup = ({
  variant = 'surface',
  size = 'medium',
  className,
  ...props
}: GroupProps) => {
  return (
    <Group
      {...props}
      className={composeRenderProps(className, (cn, renderProps) =>
        fieldGroupStyles({ ...renderProps, variant, size, className: cn })
      )}
    />
  );
};

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, ...props }, ref) => {
    return (
      <AriaInput
        ref={ref}
        {...props}
        className={composeTailwindRenderProps(
          className,
          'bg-surface-alt px-4 py-2 border-surface-alt-60 font-semibold text-surface-alt-90 placeholder:text-surface-alt-75 placeholder:font-medium'
        )}
      />
    );
  }
);

Input.displayName = 'Input';

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({ className, ...props }, ref) => {
    return (
      <AriaTextArea
        ref={ref}
        {...props}
        className={composeTailwindRenderProps(
          className,
          'bg-surface-alt px-4 py-2 border-surface-alt-60 font-semibold text-surface-alt-90 placeholder:text-surface-alt-75 placeholder:font-medium'
        )}
      />
    );
  }
);

TextArea.displayName = 'TextArea';
